import { Alert, Button, FieldBlock, Input, LoadingState, useToast } from '@orbind/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { api } from '../../lib/api.ts'
import { abrirNoNavegadorPadrao } from '../../lib/link-externo.ts'
import {
  estadoDosPassos,
  idDoProjetoValido,
  JUSTIFICATIVA_DO_ESCOPO,
  linksDoGoogle,
  numeroDoProjeto,
  PAGINAS_DO_SITE,
  projetoDosLinks,
  type Passo,
  type SituacaoDoPasso,
} from './google-calendar-passos.ts'
import './google-calendar-config.css'

/**
 * Console > Google Calendar — o passo a passo para ligar a integração
 * (SPEC-20261027-2350-integracao-google-calendar).
 *
 * Escrito para quem nunca abriu o Google Cloud: cada passo diz o que é, abre a
 * página certa do Google e diz exatamente onde clicar. O que dá para conferir,
 * o Orbind confere sozinho (a credencial com o Google; o teste lendo a agenda
 * de verdade) — e o erro diz qual passo corrigir. No fim, "Habilitar" liga o
 * Google Calendar no Calendário de todo mundo.
 */

type Situacao = Awaited<ReturnType<Awaited<ReturnType<typeof api.console['google-calendar']['$get']>>['json']>>

const erroDe = (erro: unknown) => (erro instanceof Error && erro.message ? erro.message : 'Não foi possível concluir. Tente novamente.')

const ROTULO: Record<SituacaoDoPasso, { texto: string; icone: string }> = {
  pendente: { texto: 'A fazer', icone: 'far fa-circle' },
  feito: { texto: 'Feito', icone: 'fas fa-check' },
  verificado: { texto: 'Conferido pelo Orbind', icone: 'fas fa-check-double' },
  erro: { texto: 'Precisa de ajuste', icone: 'fas fa-exclamation-triangle' },
  aguardando: { texto: 'Aguardando o Google', icone: 'far fa-hourglass-half' },
}

function Copiar({ valor }: { valor: string }) {
  const toast = useToast()
  return (
    <span className="gcal-copiar">
      <code>{valor}</code>
      <Button
        size="sm"
        variant="ghost"
        icon="far fa-copy"
        onClick={() => {
          void navigator.clipboard.writeText(valor).then(
            () => toast.push({ tone: 'success', message: 'Copiado. Agora é só colar no Google.' }),
            () => toast.push({ tone: 'danger', message: 'Não foi possível copiar; selecione o texto e copie.' }),
          )
        }}
      >
        Copiar
      </Button>
    </span>
  )
}

/** Texto com os endereços clicáveis (o erro do teste traz o link do Google que resolve). */
function comLinks(texto: string): ReactNode {
  return texto.split(/(https:\/\/[^\s]+)/).map((parte, i) =>
    /^https:\/\//.test(parte) ? (
      <a
        key={i}
        href={parte}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.preventDefault()
          void abrirNoNavegadorPadrao(parte)
        }}
      >
        Abrir no Google <i className="fas fa-external-link-alt" aria-hidden />
      </a>
    ) : (
      parte
    ),
  )
}

/** Cartão de um passo — fora da tela para não remontar (e perder o foco) a cada tecla. */
function CartaoDoPasso(props: {
  numero: number
  titulo: string
  situacao: SituacaoDoPasso
  expandido: boolean
  onAlternar: () => void
  children: ReactNode
}) {
  const { numero, titulo, situacao, expandido, onAlternar, children } = props
  const rotulo = ROTULO[situacao]
  return (
    <section className={`gcal-passo gcal-passo--${situacao}${expandido ? ' gcal-passo--aberto' : ''}`}>
      <button type="button" className="gcal-passo__cab" aria-expanded={expandido} onClick={onAlternar}>
        <span className="gcal-passo__num" aria-hidden>
          {situacao === 'feito' || situacao === 'verificado' ? <i className="fas fa-check" /> : numero}
        </span>
        <span className="gcal-passo__titulo">
          Passo {numero} · {titulo}
        </span>
        <span className={`gcal-chip gcal-chip--${situacao}`}>
          <i className={rotulo.icone} aria-hidden /> {rotulo.texto}
        </span>
        <i className={`fas fa-chevron-${expandido ? 'up' : 'down'} gcal-passo__seta`} aria-hidden />
      </button>
      {expandido ? <div className="gcal-passo__corpo">{children}</div> : null}
    </section>
  )
}

function AbrirNoGoogle({ href, children }: { href: string; children: ReactNode }) {
  return (
    <Button size="sm" variant="primary" icon="fas fa-external-link-alt" onClick={() => void abrirNoNavegadorPadrao(href)}>
      {children}
    </Button>
  )
}

export function GoogleCalendarConfigSection() {
  const toast = useToast()
  const qc = useQueryClient()
  const [aguardandoConta, setAguardandoConta] = useState<string | null | undefined>(undefined)

  const q = useQuery({
    queryKey: ['console', 'google-calendar'],
    queryFn: async () => (await api.console['google-calendar'].$get()).json(),
    // enquanto a pessoa conecta a conta numa aba nova, confere de 3 em 3 s
    refetchInterval: aguardandoConta !== undefined ? 3000 : false,
    refetchOnWindowFocus: true,
  })
  const s = q.data

  const [aberto, setAberto] = useState<Passo | null>(null)
  const [projectId, setProjectId] = useState('')
  const [clientId, setClientId] = useState('')
  const [clientSecret, setClientSecret] = useState('')
  useEffect(() => {
    if (!s) return
    setProjectId((v) => v || s.projectId)
    setClientId((v) => v || s.clientId)
  }, [s])

  const aplicar = (dados: Situacao) => qc.setQueryData(['console', 'google-calendar'], dados)
  const atualizarCalendario = () => void qc.invalidateQueries({ queryKey: ['calendar-google'] })

  const salvar = useMutation({
    mutationFn: async (corpo: {
      projectId?: string
      clientId?: string
      clientSecret?: string
      etapas?: Partial<{ projeto: boolean; api: boolean; consentimento: boolean; verificacaoEnviada: boolean; verificado: boolean }>
    }) => (await api.console['google-calendar'].$put({ json: corpo })).json(),
    onSuccess: aplicar,
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const verificarCredenciais = useMutation({
    mutationFn: async () => {
      // salva o que foi colado e já confere com o Google
      await api.console['google-calendar'].$put({ json: { clientId: clientId.trim(), clientSecret: clientSecret.trim() || undefined } })
      return (await api.console['google-calendar']['verificar-credenciais'].$post()).json()
    },
    onSuccess: (dados) => {
      aplicar(dados)
      setClientSecret('')
      toast.push({ tone: 'success', message: 'O Google reconheceu a credencial. Siga para o passo 5.' })
      setAberto('teste')
    },
    onError: (erro) => {
      void q.refetch()
      toast.push({ tone: 'danger', message: erroDe(erro) })
    },
  })

  const verificarTeste = useMutation({
    mutationFn: async () => (await api.console['google-calendar']['verificar-teste'].$post()).json(),
    onSuccess: (dados) => {
      aplicar(dados)
      if (dados.testeOkEm) {
        toast.push({ tone: 'success', message: 'Funcionou! O Orbind leu a sua agenda do Google.' })
        setAberto('habilitar')
      }
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  const conectarConta = useMutation({
    mutationFn: async () => (await api.console['google-calendar'].testar.$post()).json(),
    onSuccess: ({ url }) => {
      setAguardandoConta(s?.minhaConta?.conectadaEm ?? null)
      void abrirNoNavegadorPadrao(url)
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  // a conta conectou (ou reconectou) na outra aba: testa sozinho
  useEffect(() => {
    if (aguardandoConta === undefined || !s?.minhaConta) return
    if (s.minhaConta.conectadaEm === aguardandoConta) return
    setAguardandoConta(undefined)
    atualizarCalendario()
    verificarTeste.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aguardandoConta, s?.minhaConta?.conectadaEm])

  const habilitar = useMutation({
    mutationFn: async (habilitado: boolean) => (await api.console['google-calendar'].habilitar.$post({ json: { habilitado } })).json(),
    onSuccess: (dados) => {
      aplicar(dados)
      atualizarCalendario()
      toast.push({
        tone: 'success',
        message: dados.habilitado
          ? 'Google Calendar habilitado. Cada pessoa já pode conectar a própria agenda no Calendário.'
          : 'Google Calendar desabilitado no Calendário.',
      })
    },
    onError: (erro) => toast.push({ tone: 'danger', message: erroDe(erro) }),
  })

  if (q.isPending) return <LoadingState variant="detail" label="Carregando a configuração do Google Calendar" />
  if (q.isError || !s) {
    return (
      <Alert
        tone="danger"
        action={
          <Button size="sm" icon="fas fa-redo" onClick={() => void q.refetch()}>
            Tentar de novo
          </Button>
        }
      >
        Não foi possível carregar a configuração do Google Calendar.
      </Alert>
    )
  }

  const cabecalho = (
    <div className="adm-head">
      <div>
        <h1 className="adm-head__title">Google Calendar</h1>
        <p className="adm-head__desc">
          Ligue o Orbind ao Google Calendar: cada pessoa vê os compromissos do Google no Calendário do Orbind e envia os
          do Orbind para o Google. Os 6 primeiros passos (uns 15 minutos) já ligam a integração; o 7º pede ao Google a
          verificação do app, que tira o aviso de "app não verificado" e o limite de 100 pessoas. Só precisa ser feito
          uma vez.
        </p>
      </div>
    </div>
  )

  if (s.viaAmbiente) {
    return (
      <>
        {cabecalho}
        <Alert tone="success">
          O Google Calendar já está configurado pelas variáveis do servidor (GOOGLE_CLIENT_ID e GOOGLE_CLIENT_SECRET) e
          habilitado para todos. Para usar este assistente, remova essas variáveis do servidor.
        </Alert>
      </>
    )
  }

  const estado = estadoDosPassos(s)
  // o projeto da chave (passo 4) manda nos links; antes dela, o do passo 1
  const numero = numeroDoProjeto(s.clientId)
  const links = linksDoGoogle(projetoDosLinks(s.projectId, s.clientId))
  const idDigitadoInvalido = projectId.trim() !== '' && !idDoProjetoValido(projectId)
  const passoAberto = aberto ?? estado.atual

  const passo = ({ id, numero, titulo, children }: { id: Passo; numero: number; titulo: string; children: ReactNode }) => (
    <CartaoDoPasso
      numero={numero}
      titulo={titulo}
      situacao={estado.passos[id]}
      expandido={passoAberto === id}
      onAlternar={() => setAberto(passoAberto === id ? null : id)}
    >
      {children}
    </CartaoDoPasso>
  )

  const jaFiz = (etapa: 'projeto' | 'api' | 'consentimento', proximo: Passo) => (
    <div className="gcal-acoes">
      <Button
        size="sm"
        icon="fas fa-check"
        loading={salvar.isPending}
        onClick={() =>
          salvar.mutate({ etapas: { [etapa]: true }, ...(etapa === 'projeto' ? { projectId: projectId.trim() } : {}) }, { onSuccess: () => setAberto(proximo) })
        }
      >
        Já fiz este passo
      </Button>
    </div>
  )

  return (
    <div className="gcal">
      {cabecalho}

      <div className="gcal-progresso" role="status">
        <strong>
          {estado.concluidos} de 7 passos concluídos
        </strong>
        {s.habilitado ? (
          <span className="gcal-chip gcal-chip--verificado">
            <i className="fas fa-check-double" aria-hidden /> Habilitado para todos
          </span>
        ) : null}
      </div>

      <Alert tone="info">
        <strong>Antes de começar.</strong> Você vai usar o <strong>Google Cloud</strong>, o painel do Google para quem cria
        aplicativos — é gratuito para este uso. Entre com uma conta Google da empresa que vá continuar existindo (por
        exemplo, a de quem cuida do Orbind). Deixe esta tela aberta: os botões abrem o Google em outra aba.
      </Alert>
      <Alert tone="warning">
        <strong>O cuidado mais importante: tudo no MESMO projeto.</strong> No Google Cloud, o projeto em uso aparece no
        alto da página, logo ao lado do logo "Google Cloud". Antes de clicar em qualquer coisa nos passos 2, 3 e 4,
        confira se ali está o projeto que você criou no passo 1. Se estiver outro, clique no nome e escolha o certo. Fazer
        um passo num projeto e outro passo em outro é o erro mais comum — e o teste do passo 5 falha por causa disso.
      </Alert>

      {passo({ id: 'projeto', numero: 1, titulo: 'Criar um projeto no Google Cloud', children: (
        <>
        <p>
          O <strong>projeto</strong> é uma "pasta" no Google onde fica a ligação com o Orbind. Na primeira vez, o Google
          pode pedir para você aceitar os termos de uso e escolher o país — aceite e continue.
        </p>
        <ol>
          <li>Clique em <strong>Abrir o Google Cloud</strong> abaixo.</li>
          <li>
            Em <strong>Nome do projeto</strong>, escreva <code>Orbind</code>. Deixe "Local" como está.
          </li>
          <li>
            Logo abaixo do nome aparece o <strong>ID do projeto</strong> (algo como <code>orbind-123456</code>; se
            quiser ver ou trocar, clique em <strong>Editar</strong> ao lado dele). Copie e cole no campo abaixo — assim os
            próximos botões já abrem este projeto.
          </li>
          <li>
            Clique em <strong>Criar</strong> e espere a notificação (o sininho no alto) dizer que terminou. Na notificação,
            clique em <strong>Selecionar projeto</strong> — é isso que faz o Google passar a usar o projeto novo.
          </li>
          <li>
            Confira no alto da página: ao lado do logo "Google Cloud" tem que aparecer o nome <strong>Orbind</strong>.
          </li>
        </ol>
        <div className="gcal-acoes">
          <AbrirNoGoogle href={links.criarProjeto}>Abrir o Google Cloud</AbrirNoGoogle>
        </div>
        <FieldBlock
          label="ID do projeto (recomendado)"
          error={idDigitadoInvalido ? 'O ID tem só letras minúsculas, números e hífens (ex.: orbind-123456). Confira se copiou o ID, e não o nome.' : null}
        >
          {({ id, invalid }) => (
            <Input
              id={id}
              invalid={invalid}
              value={projectId}
              placeholder="orbind-123456"
              maxLength={100}
              onChange={(e) => setProjectId(e.target.value)}
            />
          )}
        </FieldBlock>
        {projectId.trim() === '' ? (
          <p className="gcal-nota">
            Sem o ID, os botões abrem o projeto que estiver selecionado no Google — aí é preciso conferir o nome no alto da
            página a cada passo.
          </p>
        ) : null}
        {jaFiz('projeto', 'api')}
        </>
      ) })}

      {passo({ id: 'api', numero: 2, titulo: 'Ativar a Google Calendar API', children: (
        <>
        <p>
          Isto liga, no seu projeto, a "porta" que deixa o Orbind ler e gravar compromissos no Google Agenda.
        </p>
        <ol>
          <li>Clique em <strong>Abrir a Google Calendar API</strong>.</li>
          <li>
            <strong>Antes de ativar</strong>, confira no alto da página se o projeto é o do passo 1 (o nome aparece ao
            lado do logo "Google Cloud"). Se não for, clique no nome e escolha o certo — a página recarrega no projeto
            escolhido.
          </li>
          <li>
            Clique no botão azul <strong>Ativar</strong> e espere alguns segundos. Deu certo quando o botão vira{' '}
            <strong>Gerenciar</strong> e aparece "API ativada". Se já aparecer Gerenciar logo de cara, ela já estava
            ativada neste projeto.
          </li>
        </ol>
        <div className="gcal-acoes">
          <AbrirNoGoogle href={links.ativarApi}>Abrir a Google Calendar API</AbrirNoGoogle>
        </div>
        <p className="gcal-nota">
          O Orbind confere este passo de verdade no teste do passo 5 — e, se ela estiver desligada, mostra lá o link que
          ativa no projeto certo.
        </p>
        {jaFiz('api', 'consentimento')}
        </>
      ) })}

      {passo({ id: 'consentimento', numero: 3, titulo: 'Configurar a tela de permissão do Google', children: (
        <>
        <p>
          É a tela que o Google mostra quando alguém conecta a agenda, perguntando "O Orbind quer acessar sua conta". Aqui
          você diz o nome que aparece nela e quem pode usar.
        </p>
        <ol>
          <li>
            Clique em <strong>Abrir a tela de permissão</strong> e confira o projeto no alto da página. Se aparecer "O
            Google Auth Platform ainda não está configurado", clique em <strong>Vamos começar</strong>. (Depois de
            configurado, o menu da esquerda passa a mostrar <strong>Visão geral</strong>, <strong>Branding</strong>,{' '}
            <strong>Público</strong>, <strong>Clientes</strong> e <strong>Acesso a dados</strong> — vamos usar vários
            deles.)
          </li>
          <li>
            <strong>Informações do app:</strong> nome do app <code>Orbind</code> e, em "E-mail para suporte do usuário",
            escolha o seu e-mail. Clique em <strong>Próxima</strong>.
          </li>
          <li>
            <strong>Público:</strong> escolha <strong>Externo</strong> (para funcionar com qualquer conta Google, não só as
            da sua empresa). Clique em <strong>Próxima</strong>.
          </li>
          <li>
            <strong>Dados de contato:</strong> coloque o seu e-mail, clique em <strong>Próxima</strong>, marque que concorda
            com a política e clique em <strong>Criar</strong>.
          </li>
          <li>
            Agora abra <strong>Acesso a dados</strong> (botão abaixo) e clique em <strong>Adicionar ou remover escopos</strong>.
            Abre um painel com uma lista. <strong>Role até o fim do painel</strong>: na caixa{' '}
            <strong>"Adicionar escopos manualmente"</strong>, cole o endereço abaixo, clique em{' '}
            <strong>Adicionar à tabela</strong>, depois em <strong>Atualizar</strong> e, no fim da página, em{' '}
            <strong>Salvar</strong>.
            <Copiar valor="https://www.googleapis.com/auth/calendar.events" />
            <span className="gcal-nota">
              (A lista do painel só mostra as APIs já ativadas. Se a Google Calendar API não aparece nela, o passo 2 foi
              feito em outro projeto ou a página precisa ser recarregada — mas colando o endereço acima funciona do
              mesmo jeito.)
            </span>
          </li>
          <li>
            Por último, abra <strong>Público</strong> (botão abaixo) e, em <strong>Usuários de teste</strong>, clique em{' '}
            <strong>Add users</strong> e coloque o seu e-mail Google (e o de quem for testar junto). Enquanto o app estiver
            "em teste", só esses e-mails conseguem conectar — no passo 6 explicamos como liberar para todos.
          </li>
        </ol>
        <div className="gcal-acoes">
          <AbrirNoGoogle href={links.consentimento}>Abrir a tela de permissão</AbrirNoGoogle>
          <Button size="sm" variant="ghost" icon="fas fa-external-link-alt" onClick={() => void abrirNoNavegadorPadrao(links.acessoADados)}>
            Acesso a dados
          </Button>
          <Button size="sm" variant="ghost" icon="fas fa-external-link-alt" onClick={() => void abrirNoNavegadorPadrao(links.publico)}>
            Público
          </Button>
        </div>
        {jaFiz('consentimento', 'credencial')}
        </>
      ) })}

      {passo({ id: 'credencial', numero: 4, titulo: 'Criar a chave de acesso do Orbind', children: (
        <>
        <p>
          A chave tem duas partes: o <strong>ID do cliente</strong> (pode ser visto) e a <strong>chave secreta</strong>{' '}
          (como uma senha — o Orbind guarda cifrada e nunca mostra de novo).
        </p>
        <ol>
          <li>
            Clique em <strong>Criar a chave no Google</strong> (botão abaixo). No Google, essa chave se chama{' '}
            <strong>"ID do cliente OAuth"</strong> ou <strong>"Cliente"</strong>. Confira no alto da tela se o projeto
            selecionado é o do passo 1.
            <span className="gcal-nota">
              Se o botão não abrir o formulário, chegue nele por um destes caminhos: no Google Auth Platform (o mesmo do
              passo 3), menu da esquerda <strong>Clientes</strong> → <strong>+ Criar cliente</strong>; ou menu ☰ →{' '}
              <strong>APIs e serviços</strong> → <strong>Credenciais</strong> → <strong>+ Criar credenciais</strong> →{' '}
              <strong>ID do cliente OAuth</strong>.
            </span>
          </li>
          <li>
            Em <strong>Tipo de aplicativo</strong>, escolha <strong>Aplicativo da Web</strong>. Em Nome, escreva{' '}
            <code>Orbind</code>.
          </li>
          <li>
            Em <strong>Origens JavaScript autorizadas</strong>, clique em <strong>Adicionar URI</strong> e cole:
            <Copiar valor={s.origem} />
          </li>
          <li>
            Em <strong>URIs de redirecionamento autorizados</strong>, clique em <strong>Adicionar URI</strong> e cole
            exatamente:
            <Copiar valor={s.redirectUri} />
          </li>
          <li>
            Clique em <strong>Criar</strong>. Vai abrir uma janela com o <strong>ID do cliente</strong> e a{' '}
            <strong>Chave secreta do cliente</strong> — copie cada um e cole nos campos abaixo. (Se a janela fechar, abra
            o cliente "Orbind" na lista: o ID aparece lá, e em "Chaves secretas do cliente" dá para criar outra chave.)
          </li>
        </ol>
        <div className="gcal-acoes">
          <AbrirNoGoogle href={links.criarCliente}>Criar a chave no Google</AbrirNoGoogle>
        </div>
        <div className="gcal-campos">
          <FieldBlock label="ID do cliente">
            {({ id }) => (
              <Input
                id={id}
                value={clientId}
                placeholder="123456789-abc.apps.googleusercontent.com"
                maxLength={300}
                onChange={(e) => setClientId(e.target.value)}
              />
            )}
          </FieldBlock>
          <FieldBlock label={s.temSegredo ? 'Chave secreta (já guardada — cole só para trocar)' : 'Chave secreta do cliente'}>
            {({ id }) => (
              <Input
                id={id}
                type="password"
                autoComplete="off"
                value={clientSecret}
                placeholder={s.temSegredo ? '••••••••••••' : 'GOCSPX-…'}
                maxLength={300}
                onChange={(e) => setClientSecret(e.target.value)}
              />
            )}
          </FieldBlock>
        </div>
        {numero ? (
          <p className="gcal-nota">
            <i className="fas fa-info-circle" aria-hidden /> Esta chave é do projeto nº <strong>{numero}</strong> (o
            número do começo do ID do cliente). A partir daqui, os botões do Google abrem direto esse projeto.
          </p>
        ) : null}
        {s.credenciaisOkEm ? (
          <Alert tone="success">
            O Google reconheceu esta chave. Antes de ir ao passo 5, confira que a Google Calendar API está ativada no
            projeto desta chave (se o botão do Google mostrar <strong>Gerenciar</strong>, está; se mostrar{' '}
            <strong>Ativar</strong>, clique nele).
          </Alert>
        ) : null}
        {s.credenciaisOkEm && numero ? (
          <div className="gcal-acoes">
            <Button size="sm" variant="ghost" icon="fas fa-external-link-alt" onClick={() => void abrirNoNavegadorPadrao(links.ativarApi)}>
              Conferir a API no projeto desta chave
            </Button>
          </div>
        ) : null}
        <div className="gcal-acoes">
          <Button
            size="sm"
            variant="primary"
            icon="fas fa-shield-alt"
            loading={verificarCredenciais.isPending}
            disabled={clientId.trim() === '' || (!s.temSegredo && clientSecret.trim() === '')}
            onClick={() => verificarCredenciais.mutate()}
          >
            Salvar e conferir com o Google
          </Button>
        </div>
        </>
      ) })}

      {passo({ id: 'teste', numero: 5, titulo: 'Testar com a sua conta Google', children: (
        <>
        <p>
          Agora o Orbind faz um teste de verdade: você conecta a sua agenda e ele tenta ler os compromissos de hoje.
        </p>
        <ol>
          <li>
            Clique em <strong>Conectar minha conta Google</strong>. Abre uma aba do Google: escolha a sua conta (a mesma
            que você colocou como usuário de teste no passo 3).
          </li>
          <li>
            O Google vai avisar <strong>"O Google não verificou este app"</strong>. Isso é normal enquanto o app não passa
            pela verificação do Google: clique em <strong>Avançado</strong> e depois em <strong>Acessar Orbind</strong>.
          </li>
          <li>
            O Google mostra a lista de permissões, cada uma com uma caixinha. <strong>Marque a caixa "Ver e editar
            eventos em todas as suas agendas"</strong> (ou clique em <strong>Selecionar tudo</strong>) e só então clique
            em <strong>Continuar</strong>.
            <span className="gcal-nota">
              A caixa vem desmarcada e é fácil passar direto. Sem ela a conexão não vale: o Orbind avisa "Falta marcar a
              permissão da agenda" e é só conectar de novo, desta vez marcando.
            </span>
          </li>
          <li>Volte para esta aba: o Orbind percebe sozinho e faz o teste.</li>
        </ol>
        <details className="gcal-ajuda">
          <summary>Apareceu um erro no Google?</summary>
          <ul>
            <li>
              <strong>"Erro 400: redirect_uri_mismatch"</strong> — o endereço do passo 4 não está igual. Abra o cliente
              "Orbind" no Google e confira o URI de redirecionamento (copie de novo pelo botão do passo 4).
            </li>
            <li>
              <strong>"Acesso bloqueado"</strong> ou <strong>"Erro 403: access_denied"</strong> — o seu e-mail não está
              em "Usuários de teste". Volte ao passo 3, último item.
            </li>
            <li>
              <strong>"Erro 401: invalid_client"</strong> — o ID do cliente está errado. Volte ao passo 4.
            </li>
            <li>
              <strong>Conectou, mas o teste diz que a Google Calendar API não está ativada</strong> — ela foi ativada em
              outro projeto. O aviso traz o botão <strong>Abrir no Google</strong>, que já abre o projeto da chave: clique
              em Ativar, espere 1 ou 2 minutos e clique em <strong>Testar de novo</strong>.
            </li>
          </ul>
        </details>
        <p className="gcal-nota">
          <strong>Testar de novo</strong> refaz o teste com a conta já conectada (use depois de corrigir algo no Google).{' '}
          <strong>Conectar de novo</strong> só é preciso se você trocou de conta ou se o teste pedir para reconectar.
        </p>
        {s.minhaConta ? (
          <p className="gcal-nota">
            <i className="fas fa-user-check" aria-hidden /> Conta conectada: {s.minhaConta.email || 'sua conta Google'}
          </p>
        ) : null}
        {s.testeErro ? <Alert tone="warning">{comLinks(s.testeErro)}</Alert> : null}
        {s.testeOkEm ? <Alert tone="success">Teste aprovado: o Orbind leu a sua agenda do Google.</Alert> : null}
        <div className="gcal-acoes">
          <Button
            size="sm"
            variant="primary"
            icon="fab fa-google"
            loading={conectarConta.isPending || aguardandoConta !== undefined}
            disabled={!s.credenciaisOkEm}
            onClick={() => conectarConta.mutate()}
          >
            {aguardandoConta !== undefined ? 'Aguardando você no Google…' : s.minhaConta ? 'Conectar de novo' : 'Conectar minha conta Google'}
          </Button>
          {aguardandoConta !== undefined ? (
            <Button size="sm" variant="ghost" onClick={() => setAguardandoConta(undefined)}>
              Cancelar
            </Button>
          ) : null}
          {s.minhaConta ? (
            <Button size="sm" icon="fas fa-vial" loading={verificarTeste.isPending} onClick={() => verificarTeste.mutate()}>
              Testar de novo
            </Button>
          ) : null}
        </div>
        {!s.credenciaisOkEm ? <p className="gcal-nota">Termine o passo 4 para liberar o teste.</p> : null}
        </>
      ) })}

      {passo({ id: 'habilitar', numero: 6, titulo: 'Habilitar no Calendário do Orbind', children: (
        <>
        <p>
          Com o teste aprovado, ligue o Google Calendar para todo mundo: no Calendário do Orbind aparece a seção{' '}
          <strong>Google Calendar</strong> com o botão <strong>Conectar Google</strong>, e cada pessoa conecta a própria
          agenda.
        </p>
        <Alert tone="info">
          <strong>Importante — liberar para todos:</strong> enquanto o app estiver "em teste" no Google, só os e-mails
          da lista de usuários de teste conseguem conectar, e a conexão expira a cada 7 dias. Para liberar, abra{' '}
          <strong>Público</strong> no Google e clique em <strong>Publicar app</strong> → <strong>Confirmar</strong>. Até
          100 pessoas podem usar sem a verificação do Google (elas verão o aviso "app não verificado" e clicam em
          Avançado → Acessar Orbind). Para tirar o aviso, o Google pede uma verificação do app, que pode levar algumas
          semanas.
        </Alert>
        <div className="gcal-acoes">
          <Button size="sm" variant="ghost" icon="fas fa-external-link-alt" onClick={() => void abrirNoNavegadorPadrao(links.publico)}>
            Abrir "Público" no Google
          </Button>
        </div>
        <div className="gcal-acoes">
          {s.habilitado ? (
            <Button size="sm" variant="ghost" icon="fas fa-power-off" loading={habilitar.isPending} onClick={() => habilitar.mutate(false)}>
              Desabilitar no Calendário
            </Button>
          ) : (
            <Button
              size="sm"
              variant="primary"
              icon="fas fa-calendar-check"
              loading={habilitar.isPending}
              disabled={!s.testeOkEm}
              onClick={() => habilitar.mutate(true)}
            >
              Habilitar o Google Calendar para todos
            </Button>
          )}
        </div>
        {!s.testeOkEm ? <p className="gcal-nota">O teste do passo 5 precisa ser aprovado antes.</p> : null}
        </>
      ) })}

      {passo({ id: 'verificacao', numero: 7, titulo: 'Pedir a verificação do app ao Google', children: (
        <>
          <p>
            É o que deixa o Orbind igual aos apps grandes: sem o aviso <strong>"O Google não verificou este app"</strong>{' '}
            e sem o limite de 100 pessoas. O Google confere se o app é de quem diz ser e se usa a agenda só para o que
            promete. A análise costuma levar de alguns dias a poucas semanas. Enquanto isso, o Orbind continua
            funcionando normalmente.
          </p>

          <h3 className="gcal-sub">A. Publicar o app</h3>
          <ol>
            <li>
              Abra <strong>Público</strong> e, em "Status da publicação", clique em <strong>Publicar app</strong> →{' '}
              <strong>Confirmar</strong>. Isso já libera qualquer conta Google (até 100 pessoas, com o aviso).
            </li>
          </ol>
          <div className="gcal-acoes">
            <AbrirNoGoogle href={links.publico}>Abrir "Público"</AbrirNoGoogle>
          </div>

          <h3 className="gcal-sub">B. Preencher a identidade do app</h3>
          <ol>
            <li>
              Abra <strong>Branding</strong> (Identidade visual) e preencha:
              <ul>
                <li>
                  <strong>Página inicial do app:</strong>
                  <Copiar valor={PAGINAS_DO_SITE.inicial} />
                </li>
                <li>
                  <strong>Link da Política de Privacidade:</strong>
                  <Copiar valor={PAGINAS_DO_SITE.privacidade} />
                </li>
                <li>
                  <strong>Link dos Termos de Serviço:</strong>
                  <Copiar valor={PAGINAS_DO_SITE.termos} />
                </li>
                <li>
                  <strong>Domínios autorizados:</strong> clique em <strong>Adicionar domínio</strong> e cole
                  <Copiar valor={PAGINAS_DO_SITE.dominio} />
                </li>
              </ul>
            </li>
            <li>
              O logo é opcional; se colocar, use o símbolo do Orbind em PNG de 120×120. Clique em <strong>Salvar</strong>.
            </li>
          </ol>
          <div className="gcal-acoes">
            <AbrirNoGoogle href={links.identidadeVisual}>Abrir "Branding"</AbrirNoGoogle>
          </div>

          <h3 className="gcal-sub">C. Provar que o domínio orbind.com.br é seu</h3>
          <p>
            O Google só aprova o app se tiver certeza de que o domínio <code>{PAGINAS_DO_SITE.dominio}</code> (onde estão
            as páginas da letra B) é da empresa. Ele confere isso pedindo para você colocar um código no{' '}
            <strong>DNS</strong> do domínio — a "lista de endereços" que só quem administra o domínio consegue mudar.
          </p>
          <Alert tone="info">
            <strong>Primeiro, veja se já está feito.</strong> Se alguém da empresa já usou o Search Console, o domínio
            pode estar verificado e você não precisa fazer nada. Clique em <strong>Conferir se já está verificado</strong>{' '}
            abaixo: se a página mostrar <strong>"Você é um proprietário verificado"</strong>, pule para a letra D. Se
            mostrar outra coisa (ou pedir para adicionar a propriedade), faça os passos a seguir.
          </Alert>
          <div className="gcal-acoes">
            <AbrirNoGoogle href={links.donoDoDominio}>Conferir se já está verificado</AbrirNoGoogle>
          </div>
          <ol>
            <li>
              Abra o <strong>Google Search Console</strong> com a <strong>mesma conta Google</strong> que é dona do
              projeto no Google Cloud.
            </li>
            <li>
              No alto, à esquerda, clique na caixa de propriedades e em <strong>+ Adicionar propriedade</strong>. Abrem
              duas caixas: use a da <strong>esquerda, "Domínio"</strong> (não a "Prefixo do URL"), digite só{' '}
              <code>{PAGINAS_DO_SITE.dominio}</code> — sem <code>https://</code> e sem <code>www</code> — e clique em{' '}
              <strong>Continuar</strong>.
            </li>
            <li>
              O Google mostra um código que começa com <code>google-site-verification=</code>. Clique em{' '}
              <strong>Copiar</strong> e deixe essa aba aberta.
            </li>
            <li>
              Em outra aba, abra o painel onde o DNS do domínio é administrado (no Orbind, hoje, é o{' '}
              <strong>Cloudflare</strong>: entre no domínio e vá em <strong>DNS → Registros → Adicionar registro</strong>)
              e crie um registro assim:
              <ul>
                <li>
                  <strong>Tipo:</strong> TXT
                </li>
                <li>
                  <strong>Nome:</strong> <code>@</code> <span className="gcal-nota">(quer dizer "o próprio domínio")</span>
                </li>
                <li>
                  <strong>Conteúdo:</strong> o código copiado, inteiro, incluindo <code>google-site-verification=</code>
                </li>
                <li>
                  <strong>TTL:</strong> Auto
                </li>
              </ul>
              <span className="gcal-nota">
                Já existe um TXT <code>google-site-verification</code> com outro código? Não apague — pode ficar mais de
                um. Se não souber onde fica o DNS, peça a quem cuida do domínio para "criar um registro TXT com este
                valor".
              </span>
            </li>
            <li>
              Volte ao Search Console e clique em <strong>Verificar</strong>. Se falhar, espere de 5 a 10 minutos e
              clique de novo — quase sempre é só o DNS demorando para atualizar.
            </li>
            <li>
              Deu certo quando aparece <strong>"Propriedade verificada"</strong> e o Search Console abre na{' '}
              <strong>Visão geral</strong> com <code>{PAGINAS_DO_SITE.dominio}</code> no alto, à esquerda. O aviso "Dados
              em processamento: volte em mais ou menos um dia" é normal e não tem a ver com a verificação — pode seguir.
              <span className="gcal-nota">
                Se o domínio foi verificado com outra conta Google, entre em <strong>Configurações → Usuários e
                permissões</strong> e adicione a conta dona do projeto como <strong>Proprietário</strong>. O Google só
                aceita o domínio se quem é dono do projeto também for dono dele no Search Console.
              </span>
            </li>
          </ol>
          <div className="gcal-acoes">
            <AbrirNoGoogle href={links.searchConsole}>Abrir o Search Console</AbrirNoGoogle>
          </div>

          <h3 className="gcal-sub">D. Gravar um vídeo curto (2 a 3 minutos)</h3>
          <p>O Google pede um vídeo mostrando o app usando a agenda. Grave a tela (pode ser com o celular apontado) mostrando:</p>
          <ol>
            <li>
              O Calendário do Orbind e o clique em <strong>Conectar Google</strong>, até a tela de permissão do Google
              (o Google costuma pedir essa tela em <strong>inglês</strong>: troque o idioma no rodapé da tela de login do
              Google antes de gravar).
            </li>
            <li>Os compromissos do Google aparecendo no Calendário do Orbind.</li>
            <li>
              A criação de um evento com <strong>"Enviar também para o meu Google Calendar"</strong> marcado, e o mesmo
              evento aparecendo no Google Agenda.
            </li>
          </ol>
          <p>
            Suba no YouTube como <strong>"Não listado"</strong> e guarde o link.
          </p>

          <h3 className="gcal-sub">E. Enviar para verificação</h3>
          <ol>
            <li>
              Abra a <strong>Central de verificação</strong> e clique em <strong>Preparar para verificação</strong> (ou
              "Enviar para verificação").
            </li>
            <li>
              Quando pedir a justificativa do escopo <code>calendar.events</code>, cole o texto abaixo (em inglês, que é
              o idioma da análise):
              <Copiar valor={JUSTIFICATIVA_DO_ESCOPO} />
            </li>
            <li>Cole o link do vídeo, confirme e envie. O Google responde por e-mail; fique de olho também no spam.</li>
          </ol>
          <div className="gcal-acoes">
            <AbrirNoGoogle href={links.centralDeVerificacao}>Abrir a Central de verificação</AbrirNoGoogle>
          </div>

          <Alert tone="info">
            O Orbind não consegue consultar no Google o andamento da verificação. Marque abaixo quando enviar e quando
            o Google aprovar, para deixar registrado aqui.
          </Alert>
          <div className="gcal-acoes">
            {!s.etapas.verificacaoEnviada ? (
              <Button
                size="sm"
                icon="fas fa-paper-plane"
                loading={salvar.isPending}
                onClick={() => salvar.mutate({ etapas: { verificacaoEnviada: true } })}
              >
                Já enviei para o Google
              </Button>
            ) : null}
            {s.etapas.verificacaoEnviada && !s.etapas.verificado ? (
              <Button
                size="sm"
                variant="primary"
                icon="fas fa-check-double"
                loading={salvar.isPending}
                onClick={() => salvar.mutate({ etapas: { verificado: true } })}
              >
                O Google aprovou
              </Button>
            ) : null}
            {s.etapas.verificado ? (
              <Alert tone="success">App verificado pelo Google. Ninguém mais vê o aviso de app não verificado.</Alert>
            ) : null}
          </div>
        </>
      ) })}
    </div>
  )
}
